// ============================================================================
// Mining System Types - Ore tiers, pickaxe tiers, and mining mechanics
// ============================================================================
import { ItemType } from '../items/types.js';
import { ObjectType } from '../objects/types.js';
// All ore types with their mining requirements and XP gains
export const ORE_TIERS = {
    // Tier 1 - Beginner ores (hardness 1)
    [ObjectType.ORE_TIN]: {
        objectType: ObjectType.ORE_TIN,
        itemType: ItemType.TIN_ORE,
        miningLevelRequired: 1,
        baseXpGain: 10,
        baseMineTimeMs: 2000,
        hardness: 1,
    },
    [ObjectType.ORE_TIN_LARGE]: {
        objectType: ObjectType.ORE_TIN_LARGE,
        itemType: ItemType.TIN_ORE,
        miningLevelRequired: 1,
        baseXpGain: 10,
        baseMineTimeMs: 2000,
        hardness: 1,
    },
    [ObjectType.ORE_COPPER]: {
        objectType: ObjectType.ORE_COPPER,
        itemType: ItemType.COPPER_ORE,
        miningLevelRequired: 1,
        baseXpGain: 12,
        baseMineTimeMs: 2500,
        hardness: 1,
    },
    [ObjectType.ORE_COPPER_LARGE]: {
        objectType: ObjectType.ORE_COPPER_LARGE,
        itemType: ItemType.COPPER_ORE,
        miningLevelRequired: 1,
        baseXpGain: 12,
        baseMineTimeMs: 2500,
        hardness: 1,
    },
    // Tier 2 - Iron/Coal (hardness 2)
    [ObjectType.ORE_IRON]: {
        objectType: ObjectType.ORE_IRON,
        itemType: ItemType.IRON_ORE,
        miningLevelRequired: 15,
        baseXpGain: 25,
        baseMineTimeMs: 4000,
        hardness: 2,
    },
    [ObjectType.ORE_IRON_LARGE]: {
        objectType: ObjectType.ORE_IRON_LARGE,
        itemType: ItemType.IRON_ORE,
        miningLevelRequired: 15,
        baseXpGain: 25,
        baseMineTimeMs: 4000,
        hardness: 2,
    },
    [ObjectType.ORE_COAL]: {
        objectType: ObjectType.ORE_COAL,
        itemType: ItemType.COAL,
        miningLevelRequired: 20,
        baseXpGain: 20,
        baseMineTimeMs: 3500,
        hardness: 2,
    },
    [ObjectType.ORE_COAL_LARGE]: {
        objectType: ObjectType.ORE_COAL_LARGE,
        itemType: ItemType.COAL,
        miningLevelRequired: 20,
        baseXpGain: 20,
        baseMineTimeMs: 3500,
        hardness: 2,
    },
    // Tier 3 - Silver/Gold (hardness 3)
    [ObjectType.ORE_SILVER]: {
        objectType: ObjectType.ORE_SILVER,
        itemType: ItemType.SILVER_ORE,
        miningLevelRequired: 30,
        baseXpGain: 40,
        baseMineTimeMs: 5000,
        hardness: 3,
    },
    [ObjectType.ORE_SILVER_LARGE]: {
        objectType: ObjectType.ORE_SILVER_LARGE,
        itemType: ItemType.SILVER_ORE,
        miningLevelRequired: 30,
        baseXpGain: 40,
        baseMineTimeMs: 5000,
        hardness: 3,
    },
    [ObjectType.ORE_GOLD]: {
        objectType: ObjectType.ORE_GOLD,
        itemType: ItemType.GOLD_ORE,
        miningLevelRequired: 40,
        baseXpGain: 60,
        baseMineTimeMs: 6000,
        hardness: 3,
    },
    [ObjectType.ORE_GOLD_LARGE]: {
        objectType: ObjectType.ORE_GOLD_LARGE,
        itemType: ItemType.GOLD_ORE,
        miningLevelRequired: 40,
        baseXpGain: 60,
        baseMineTimeMs: 6000,
        hardness: 3,
    },
    // Tier 4 - Mithril (hardness 4)
    [ObjectType.ORE_MITHRIL]: {
        objectType: ObjectType.ORE_MITHRIL,
        itemType: ItemType.MITHRIL_ORE,
        miningLevelRequired: 55,
        baseXpGain: 100,
        baseMineTimeMs: 8000,
        hardness: 4,
    },
    [ObjectType.ORE_MITHRIL_LARGE]: {
        objectType: ObjectType.ORE_MITHRIL_LARGE,
        itemType: ItemType.MITHRIL_ORE,
        miningLevelRequired: 55,
        baseXpGain: 100,
        baseMineTimeMs: 8000,
        hardness: 4,
    },
    // Tier 5 - Starmetal (hardness 5)
    [ObjectType.ORE_STARMETAL]: {
        objectType: ObjectType.ORE_STARMETAL,
        itemType: ItemType.STARMETAL_ORE,
        miningLevelRequired: 70,
        baseXpGain: 150,
        baseMineTimeMs: 12000,
        hardness: 5,
    },
    [ObjectType.ORE_STARMETAL_LARGE]: {
        objectType: ObjectType.ORE_STARMETAL_LARGE,
        itemType: ItemType.STARMETAL_ORE,
        miningLevelRequired: 70,
        baseXpGain: 150,
        baseMineTimeMs: 12000,
        hardness: 5,
    },
};
// All pickaxe types with their capabilities
export const PICKAXE_TIERS = {
    [ItemType.WOODEN_PICKAXE]: {
        itemType: ItemType.WOODEN_PICKAXE,
        miningSpeedMultiplier: 1.0,
        maxHardness: 1,
        miningLevelRequired: 1,
    },
    [ItemType.STONE_PICKAXE]: {
        itemType: ItemType.STONE_PICKAXE,
        miningSpeedMultiplier: 1.3,
        maxHardness: 1,
        miningLevelRequired: 1,
    },
    [ItemType.BRONZE_PICKAXE]: {
        itemType: ItemType.BRONZE_PICKAXE,
        miningSpeedMultiplier: 1.6,
        maxHardness: 2,
        miningLevelRequired: 10,
    },
    [ItemType.IRON_PICKAXE]: {
        itemType: ItemType.IRON_PICKAXE,
        miningSpeedMultiplier: 2.0,
        maxHardness: 2,
        miningLevelRequired: 15,
    },
    [ItemType.STEEL_PICKAXE]: {
        itemType: ItemType.STEEL_PICKAXE,
        miningSpeedMultiplier: 2.5,
        maxHardness: 3,
        miningLevelRequired: 25,
    },
    [ItemType.SILVER_PICKAXE]: {
        itemType: ItemType.SILVER_PICKAXE,
        miningSpeedMultiplier: 3.0,
        maxHardness: 3,
        miningLevelRequired: 35,
    },
    [ItemType.MITHRIL_PICKAXE]: {
        itemType: ItemType.MITHRIL_PICKAXE,
        miningSpeedMultiplier: 4.0,
        maxHardness: 4,
        miningLevelRequired: 55,
    },
    [ItemType.STARMETAL_PICKAXE]: {
        itemType: ItemType.STARMETAL_PICKAXE,
        miningSpeedMultiplier: 5.0,
        maxHardness: 5,
        miningLevelRequired: 70,
    },
};
// ============================================================================
// Large Ore Deposit Respawn Configuration
// ============================================================================
// Respawn times for large ore deposits (in milliseconds)
export const ORE_RESPAWN_TIMES = {
    [ObjectType.ORE_TIN_LARGE]: 10 * 60 * 1000, // 10 minutes
    [ObjectType.ORE_COPPER_LARGE]: 10 * 60 * 1000, // 10 minutes
    [ObjectType.ORE_IRON_LARGE]: 15 * 60 * 1000, // 15 minutes
    [ObjectType.ORE_COAL_LARGE]: 15 * 60 * 1000, // 15 minutes
    [ObjectType.ORE_SILVER_LARGE]: 20 * 60 * 1000, // 20 minutes
    [ObjectType.ORE_GOLD_LARGE]: 20 * 60 * 1000, // 20 minutes
    [ObjectType.ORE_MITHRIL_LARGE]: 25 * 60 * 1000, // 25 minutes
    [ObjectType.ORE_STARMETAL_LARGE]: 30 * 60 * 1000, // 30 minutes
};
// Set of large ore deposit types that respawn
export const LARGE_ORE_DEPOSITS = new Set([
    ObjectType.ORE_TIN_LARGE,
    ObjectType.ORE_COPPER_LARGE,
    ObjectType.ORE_IRON_LARGE,
    ObjectType.ORE_COAL_LARGE,
    ObjectType.ORE_SILVER_LARGE,
    ObjectType.ORE_GOLD_LARGE,
    ObjectType.ORE_MITHRIL_LARGE,
    ObjectType.ORE_STARMETAL_LARGE,
]);
// ============================================================================
// Helper Functions
// ============================================================================
/**
 * Get ore tier configuration for an object type
 */
export const getOreTier = ({ objectType, }) => {
    return ORE_TIERS[objectType] ?? null;
};
/**
 * Get pickaxe tier configuration for an item type
 */
export const getPickaxeTier = ({ itemType, }) => {
    return PICKAXE_TIERS[itemType] ?? null;
};
/**
 * Check if an object type is an ore deposit
 */
export const isOreDeposit = ({ objectType }) => {
    return ORE_TIERS[objectType] !== undefined;
};
/**
 * Object types that require a pickaxe to harvest (rocks and ores)
 */
const MINEABLE_OBJECTS = new Set([
    // Rocks
    ObjectType.ROCK_SMALL,
    ObjectType.ROCK_MEDIUM,
    ObjectType.ROCK_LARGE,
    ObjectType.BOULDER,
]);
/**
 * Check if an object type requires a pickaxe to harvest
 */
export const requiresPickaxe = ({ objectType }) => {
    // Ores require pickaxe
    if (isOreDeposit({ objectType })) {
        return true;
    }
    // Rocks require pickaxe
    return MINEABLE_OBJECTS.has(objectType);
};
/**
 * Check if an object type is a large (respawning) ore deposit
 */
export const isLargeOreDeposit = ({ objectType }) => {
    return LARGE_ORE_DEPOSITS.has(objectType);
};
/**
 * Get respawn time for a large ore deposit (0 if not respawnable)
 */
export const getOreRespawnTime = ({ objectType }) => {
    return ORE_RESPAWN_TIMES[objectType] ?? 0;
};
/**
 * Check if a pickaxe can mine a specific ore
 */
export const canPickaxeMineOre = ({ pickaxeType, oreObjectType, }) => {
    const pickaxeTier = PICKAXE_TIERS[pickaxeType];
    const oreTier = ORE_TIERS[oreObjectType];
    if (!pickaxeTier || !oreTier) {
        return false;
    }
    return pickaxeTier.maxHardness >= oreTier.hardness;
};
//# sourceMappingURL=types.js.map